import { useMemo } from 'react';
import { useQuoteSheetDashboard } from '@/api/procurement/useQuoteSheetDashboard';
import {
  QuoteSheetDashboardFilters,
  QuoteSheetLineItemDTO,
} from '@/api/procurement/quote-sheets-types';
import { useQuoteSheetTableContext } from './quotes-sheet-table.context';
import {
  AggregatedRowData,
  ProductVariantBreakdownTypes,
  UnaggregatedRowData,
  SupplierRowData,
  SortOption,
  isAggregatedRowData,
} from './utils';
import {
  getEffectiveProductId,
  getEffectiveProductVariantId,
  getProductName,
  getProductNamePreferProduct,
} from '../utils';

function sortByLabelAsc<T extends { label: string }>(a: T, b: T): number {
  return a.label.localeCompare(b.label);
}

function sortByPriceAsc<
  T extends AggregatedRowData | UnaggregatedRowData | SupplierRowData,
>(a: T, b: T): number {
  const priceA = isAggregatedRowData(a)
    ? (a.aggregatedPricesByDate[a.aggregatedPricesByDate.length - 1]
        ?.priceFOBAvg ?? null)
    : (a.pricesByDate?.[a.pricesByDate.length - 1]?.priceFOB ?? null);
  const priceB = isAggregatedRowData(b)
    ? (b.aggregatedPricesByDate[b.aggregatedPricesByDate.length - 1]
        ?.priceFOBAvg ?? null)
    : (b.pricesByDate?.[b.pricesByDate.length - 1]?.priceFOB ?? null);

  if (priceA === null && priceB === null) return sortByLabelAsc(a, b);
  if (priceA === null) return 1;
  if (priceB === null) return -1;

  return priceA - priceB;
}

function sortByPriceDesc<
  T extends AggregatedRowData | UnaggregatedRowData | SupplierRowData,
>(a: T, b: T): number {
  const priceA = isAggregatedRowData(a)
    ? (a.aggregatedPricesByDate[a.aggregatedPricesByDate.length - 1]
        ?.priceFOBAvg ?? null)
    : (a.pricesByDate?.[a.pricesByDate.length - 1]?.priceFOB ?? null);
  const priceB = isAggregatedRowData(b)
    ? (b.aggregatedPricesByDate[b.aggregatedPricesByDate.length - 1]
        ?.priceFOBAvg ?? null)
    : (b.pricesByDate?.[b.pricesByDate.length - 1]?.priceFOB ?? null);
  if (priceA === null && priceB === null) return sortByLabelAsc(a, b);
  if (priceA === null) return 1;
  if (priceB === null) return -1;
  return priceB - priceA;
}

const sortByToSortFunction: Record<
  SortOption,
  typeof sortByLabelAsc | typeof sortByPriceAsc | typeof sortByPriceDesc
> = {
  alphabetical: sortByLabelAsc,
  'price-asc': sortByPriceAsc,
  'price-desc': sortByPriceDesc,
};

function calculatePricesByDate({
  allDates,
  itemsByDate,
}: {
  allDates: string[];
  itemsByDate: { [date: string]: QuoteSheetLineItemDTO[] };
}) {
  const pricesByDate: {
    date: string;
    priceFOB: number | null;
    priceQuoteSheetId: string;
  }[] = [];
  let previousPrice: number | null = null;
  let previousQuoteSheetId = '';

  allDates.forEach((date) => {
    const dateItems = itemsByDate[date] || [];
    if (dateItems.length === 0) {
      // If no data for this date, use previous values if available
      pricesByDate.push({
        date,
        priceFOB: previousPrice,
        priceQuoteSheetId: previousQuoteSheetId,
      });
    } else {
      // Use the first item's price if multiple items exist for the same date/vendor
      const item = dateItems[0];
      const price = Number.parseFloat(item.unitPrice as string);
      const priceFOB = Number.isNaN(price) ? null : price;
      pricesByDate.push({
        date,
        priceFOB,
        priceQuoteSheetId: item.quoteSheetId,
      });
      previousPrice = priceFOB;
      previousQuoteSheetId = item.quoteSheetId;
    }
  });

  // Calculate percentage changes
  const pricesByDateWithChanges = pricesByDate.map((price, index) => {
    if (index === 0 || price.priceFOB === null) {
      return { ...price, pctChange: undefined };
    }
    const prevPrice = pricesByDate[index - 1].priceFOB;
    if (prevPrice === null) {
      return { ...price, pctChange: undefined };
    }
    const pctChange = ((price.priceFOB - prevPrice) / prevPrice) * 100;
    return { ...price, pctChange: Math.round(pctChange * 100) / 100 };
  });

  return pricesByDateWithChanges;
}

function calculateAggregatedPricesByDate({
  allDates,
  itemsByDate,
}: {
  allDates: string[];
  itemsByDate: { [date: string]: QuoteSheetLineItemDTO[] };
}) {
  // Calculate price statistics for each date
  const pricesByDate: {
    date: string;
    priceFOBAvg: number | null;
    priceFOBMed: number | null;
    priceFOBMin: number | null;
    priceFOBMax: number | null;
  }[] = [];

  let previousData: {
    priceFOBAvg: number | null;
    priceFOBMed: number | null;
    priceFOBMin: number | null;
    priceFOBMax: number | null;
  } | null = null;

  allDates.forEach((date) => {
    const dateItems = itemsByDate[date] || [];
    const prices = dateItems
      .map((item) => Number.parseFloat(item.unitPrice as string))
      .filter(
        (price): price is number =>
          price !== null && price !== undefined && !Number.isNaN(price),
      );

    if (prices.length === 0) {
      // If no data for this date, use previous values if available
      if (previousData) {
        pricesByDate.push({
          date,
          priceFOBAvg: previousData.priceFOBAvg,
          priceFOBMed: previousData.priceFOBMed,
          priceFOBMin: previousData.priceFOBMin,
          priceFOBMax: previousData.priceFOBMax,
        });
      } else {
        // First date with no data, use null values
        pricesByDate.push({
          date,
          priceFOBAvg: null,
          priceFOBMed: null,
          priceFOBMin: null,
          priceFOBMax: null,
        });
      }
    } else {
      const sortedPrices = prices.sort((a, b) => a - b);
      const avg = prices.reduce((sum, price) => sum + price, 0) / prices.length;
      const med =
        sortedPrices.length % 2 === 0
          ? (sortedPrices[sortedPrices.length / 2 - 1] +
              sortedPrices[sortedPrices.length / 2]) /
            2
          : sortedPrices[Math.floor(sortedPrices.length / 2)];

      const currentData = {
        date,
        priceFOBAvg: Math.round(avg * 100) / 100,
        priceFOBMed: Math.round(med * 100) / 100,
        priceFOBMin: Math.min(...prices),
        priceFOBMax: Math.max(...prices),
      };

      pricesByDate.push(currentData);
      previousData = currentData;
    }
  });

  return pricesByDate;
}

function buildSupplierRows({
  depth,
  lineItemsData,
  breakdownChainRest,
  allDates,
  sortBy,
}: {
  depth: number;
  lineItemsData: QuoteSheetLineItemDTO[];
  breakdownChainRest: ProductVariantBreakdownTypes[];
  allDates: string[];
  sortBy: SortOption;
}): SupplierRowData[] {
  const hasChildren = breakdownChainRest.length > 0;
  // Group items by vendor for supplier-level data
  const vendorGroups: { [vendorId: string]: QuoteSheetLineItemDTO[] } = {};
  lineItemsData.forEach((item) => {
    const vendorId = item.quoteSheet?.supplier?.id || 'unknown';
    if (!vendorGroups[vendorId]) {
      vendorGroups[vendorId] = [];
    }
    vendorGroups[vendorId].push(item);
  });

  if (!hasChildren) {
    return Object.values(vendorGroups)
      .map((vendorItems) => {
        const name =
          vendorItems[0].quoteSheet?.supplier?.name || 'Unknown Supplier';
        const id = vendorItems[0].quoteSheet?.supplier?.id || 'unknown';

        return {
          depth,
          key: id,
          label: name,
          vendor: {
            id,
            name,
          },
          pricesByDate: calculatePricesByDate({
            allDates,
            itemsByDate: vendorItems.reduce(
              (acc, item) => {
                const dateKey = new Date(item.createdAt).toDateString();
                if (!acc[dateKey]) {
                  acc[dateKey] = [];
                }
                acc[dateKey].push(item);
                return acc;
              },
              {} as { [date: string]: typeof vendorItems },
            ),
          }),
        };
      })
      .sort(sortByToSortFunction[sortBy]);
  }

  // Create supplier row data
  const supplierRows: SupplierRowData[] = Object.values(vendorGroups)
    .map((vendorItems) => {
      const vendor = vendorItems[0].quoteSheet?.supplier || {
        id: 'unknown',
        name: 'Unknown Supplier',
      };

      // Group vendor items by date
      const vendorItemsByDate: { [date: string]: typeof vendorItems } = {};
      vendorItems.forEach((item) => {
        const dateKey = new Date(item.createdAt).toDateString();
        if (!vendorItemsByDate[dateKey]) {
          vendorItemsByDate[dateKey] = [];
        }
        vendorItemsByDate[dateKey].push(item);
      });

      return {
        depth,
        key: vendor.id,
        label: vendor.name,
        children: buildDataBreakdown({
          depth: depth + 1,
          breakdownChain: breakdownChainRest,
          lineItemsData: vendorItems,
          allDates,
          sortBy,
        }),
        vendor: { id: vendor.id, name: vendor.name },
        pricesByDate: calculatePricesByDate({
          allDates,
          itemsByDate: vendorItemsByDate,
        }),
      };
    })
    .sort(sortByToSortFunction[sortBy]);

  return supplierRows;
}

function buildEnumeratedVariantRows({
  depth,
  lineItemsData,
  breakdownChainRest,
  allDates,
  sortBy,
}: {
  depth: number;
  lineItemsData: QuoteSheetLineItemDTO[];
  breakdownChainRest: ProductVariantBreakdownTypes[];
  allDates: string[];
  sortBy: SortOption;
}): UnaggregatedRowData[] | AggregatedRowData[] {
  const hasChildren = breakdownChainRest.length > 0;
  // Group items by variant for supplier-level data
  const variantGroups: { [variantId: string]: QuoteSheetLineItemDTO[] } = {};
  lineItemsData.forEach((item) => {
    const variantId =
      getEffectiveProductVariantId(item) || `unknown-${getProductName(item)}`;
    if (!variantGroups[variantId]) {
      variantGroups[variantId] = [];
    }
    variantGroups[variantId].push(item);
  });

  // return aggregated data if there are children
  if (hasChildren) {
    return Object.entries(variantGroups)
      .map(([variantId, variantItems]) => ({
        depth,
        key: variantId,
        label: variantId.startsWith('unknown-')
          ? `Unmatched Variant (${getProductName(variantItems[0])})`
          : getProductName(variantItems[0]),
        children: buildDataBreakdown({
          depth: depth + 1,
          breakdownChain: breakdownChainRest,
          lineItemsData: variantItems,
          allDates,
          sortBy,
        }),
        aggregatedPricesByDate: calculateAggregatedPricesByDate({
          allDates,
          itemsByDate: variantItems.reduce(
            (acc, item) => {
              const dateKey = new Date(item.createdAt).toDateString();
              (acc[dateKey] ||= []).push(item);
              return acc;
            },
            {} as { [date: string]: QuoteSheetLineItemDTO[] },
          ),
        }),
      }))
      .sort(sortByToSortFunction[sortBy]);
  }

  return Object.entries(variantGroups)
    .map(([variantId, variantItems]) => ({
      depth,
      key: variantId,
      label: getProductName(variantItems[0]),
      pricesByDate: calculatePricesByDate({
        allDates,
        itemsByDate: variantItems.reduce(
          (acc, item) => {
            const dateKey = new Date(item.createdAt).toDateString();
            (acc[dateKey] ||= []).push(item);
            return acc;
          },
          {} as { [date: string]: QuoteSheetLineItemDTO[] },
        ),
      }),
    }))
    .sort(sortByLabelAsc);
}

function buildProductRows({
  depth,
  lineItemsData,
  breakdownChainRest,
  allDates,
  sortBy,
}: {
  depth: number;
  lineItemsData: QuoteSheetLineItemDTO[];
  breakdownChainRest: ProductVariantBreakdownTypes[];
  allDates: string[];
  sortBy: SortOption;
}): UnaggregatedRowData[] {
  const lineItemsByProductId: { [productId: string]: QuoteSheetLineItemDTO[] } =
    {};
  lineItemsData.forEach((item) => {
    const productId =
      getEffectiveProductId(item) || `unknown-${getProductName(item)}`;
    if (!lineItemsByProductId[productId]) {
      lineItemsByProductId[productId] = [];
    }
    lineItemsByProductId[productId].push(item);
  });

  return Object.entries(lineItemsByProductId)
    .map(([productId, productItems]) => ({
      depth,
      key: productId,
      label: productId.startsWith('unknown-')
        ? `Unmatched Product (${getProductNamePreferProduct(productItems[0])})`
        : getProductNamePreferProduct(productItems[0]),
      children:
        breakdownChainRest.length > 0
          ? buildDataBreakdown({
              depth: depth + 1,
              breakdownChain: breakdownChainRest,
              lineItemsData: productItems,
              allDates,
              sortBy,
            })
          : [],
    }))
    .sort(sortByLabelAsc);
}

function buildDataBreakdown({
  depth,
  breakdownChain,
  lineItemsData,
  allDates,
  sortBy,
}: {
  depth: number;
  breakdownChain: ProductVariantBreakdownTypes[];
  lineItemsData: QuoteSheetLineItemDTO[];
  allDates: string[];
  sortBy: SortOption;
}): SupplierRowData[] | AggregatedRowData[] | UnaggregatedRowData[] {
  if (breakdownChain.length === 0) {
    return [];
  }

  const [current, ...breakdownRest] = breakdownChain;

  switch (current) {
    case ProductVariantBreakdownTypes.SUPPLIER:
      return buildSupplierRows({
        depth,
        lineItemsData,
        breakdownChainRest: breakdownRest,
        allDates,
        sortBy,
      });
    case ProductVariantBreakdownTypes.ENUMERATED_VARIANT:
      return buildEnumeratedVariantRows({
        depth,
        lineItemsData,
        breakdownChainRest: breakdownRest,
        allDates,
        sortBy,
      });
    // case ProductVariantBreakdownTypes.RECURSIVE_VARIANT:
    //   return buildRecursiveVariantRows({
    //     depth,
    //     lineItemsData,
    //     breakdownChainRest: breakdownRest,
    //     allDates,
    //   });
    case ProductVariantBreakdownTypes.PRODUCT:
      return buildProductRows({
        depth,
        lineItemsData,
        breakdownChainRest: breakdownRest,
        allDates,
        sortBy,
      });
  }

  return [];
}

export function useTransformedQuoteSheetData(
  filters: QuoteSheetDashboardFilters &
    Required<Pick<QuoteSheetDashboardFilters, 'dateFrom' | 'dateTo'>>,
  sortBy: SortOption = 'alphabetical',
): AggregatedRowData[] | UnaggregatedRowData[] | SupplierRowData[] | undefined {
  const {
    data: apiResponse,
    isLoading: _isLoading,
    error: _error,
  } = useQuoteSheetDashboard(filters);

  const { productVariantBreakdown, implicitRestOfBreakdown } =
    useQuoteSheetTableContext();

  const breakdownChain = useMemo(
    () => [...productVariantBreakdown, ...implicitRestOfBreakdown],
    [productVariantBreakdown, implicitRestOfBreakdown],
  );

  const transformedData:
    | UnaggregatedRowData[]
    | AggregatedRowData[]
    | SupplierRowData[]
    | undefined = useMemo(() => {
    if (
      !apiResponse?.quoteSheetLineItemsByDate ||
      apiResponse.quoteSheetLineItemsByDate.length === 0
    ) {
      return undefined;
    }

    return buildDataBreakdown({
      depth: 0,
      breakdownChain,
      lineItemsData: apiResponse.quoteSheetLineItemsByDate,
      allDates: enumerateDates(filters?.dateFrom, filters?.dateTo),
      sortBy,
    });
  }, [apiResponse, filters, breakdownChain, sortBy]);

  return transformedData;
}

function enumerateDates(dateFrom: Date, dateTo: Date): string[] {
  const dates: string[] = [];
  const startDate = new Date(dateFrom);
  const endDate = new Date(dateTo);
  const currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    dates.push(currentDate.toDateString());
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
}
